import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { dayKey } from './reportData';
import { summarize } from './reportSummary';
import {
  C, FONT, PAGE_W, PAD, CONTENT_W, RUN_H, FOOT_H, BOTTOM, PAGE_H, rgb, loadFonts, text, lines, lineH, fill, stroke,
  withOpacity, ring, sectionHead, tag, time, dayLong, weekdayName, weekdayShort, dayNum, dayMonthYear, shortStamp,
  keyToDate, periodLabel, minutesLabel, fetchMapImage,
} from './pdfReportKit';

const TAGLINE = 'La sua sicurezza, la tua serenità';
const MOOD_NAME = { serena: 'Serena', neutra: 'Neutra', triste: 'Triste' };
const MOOD_COLOR = { serena: C.umore, neutra: C.neutra, triste: C.sos };
const KIND = {
  medicine: { label: 'Farmaco', color: C.farmaci }, meal: { label: 'Pasto', color: C.pasti },
  activity: { label: 'Attività', color: C.attivita }, walk: { label: 'Passeggiata', color: C.passo },
  mood: { label: 'Umore', color: C.umore }, note: { label: 'Nota', color: C.nota },
  visit: { label: 'Visita', color: C.brandDeep }, sos: { label: 'Scudo', color: C.sos },
};
const MAPPED_WALKS = 2;
const NOT_MOOD = { fisico: 'Stato fisico', stato_fisico: 'Stato fisico', physical: 'Stato fisico', health: 'Stato fisico', appetito: 'Appetito', appetite: 'Appetito', nutrition: 'Appetito' };
// Dettaglio dell'umore solo se aggiunge qualcosa al livello (es. "Calmo", non "Sereno")
const moodExtra = (m) => (m.level && m.label && !m.label.toLowerCase().startsWith(MOOD_NAME[m.level].toLowerCase().slice(0, 5)) ? m.label : '');

// ---------- eventi del diario e del registro ----------
const buildEvents = (model, cg, walkPage) => {
  const events = [];
  const meds = {};
  model.medicines.forEach((m) => {
    const k = m.time.getTime();
    meds[k] = meds[k] || { time: m.time, kind: 'medicine', names: [], notes: new Set() };
    meds[k].names.push(m.dose ? `${m.name} ${m.dose}` : m.name);
    if (m.note) meds[k].notes.add(m.note);
  });
  Object.values(meds).forEach((m) => events.push({ time: m.time, kind: 'medicine', title: m.names.join(', '), small: [...m.notes].join(' — ') }));
  model.meals.forEach((m) => events.push({ time: m.time, kind: 'meal', title: m.type, small: m.description }));
  model.activities.forEach((a) => events.push({ time: a.time, kind: 'activity', title: a.note && a.note.toLowerCase().startsWith(a.name.toLowerCase()) ? a.note : a.name, small: a.note && !a.note.toLowerCase().startsWith(a.name.toLowerCase()) ? a.note : '' }));
  model.walks.forEach((w, i) => events.push({
    time: w.start, kind: 'walk',
    title: w.minutes != null ? `Passeggiata, ${minutesLabel(w.minutes)}` : 'Passeggiata',
    small: [w.place, i < MAPPED_WALKS && w.pins.length && walkPage ? `mappa a pagina ${walkPage}` : ''].filter(Boolean).join(' — '),
  }));
  model.moods.forEach((m) => {
    const title = m.level ? MOOD_NAME[m.level] : m.label;
    const extra = m.level ? moodExtra(m) : '';
    events.push({ time: m.time, kind: 'mood', title, small: [extra, m.note].filter(Boolean).join(' — '), color: m.level ? MOOD_COLOR[m.level] : C.umore });
  });
  model.notes.forEach((n) => events.push({ time: n.time, kind: 'note', title: `Nota di ${cg}`, small: n.text }));
  model.visits.forEach((v) => events.push({ time: v.time, kind: 'visit', title: `Visita: ${v.doctor}${v.specialty ? `, ${v.specialty.toLowerCase()}` : ''}`, small: v.outcome }));
  model.sos.forEach((s) => events.push({ time: s.time, kind: 'sos', title: s.kind === 'Scudo' ? 'Allarme Scudo' : 'SOS', small: s.description }));
  return events.sort((a, b) => a.time - b.time);
};

// ---------- tabelle ----------
const table = (doc, startY, head, body, columnStyles) => {
  autoTable(doc, {
    startY,
    head: [head.map((h) => h.toUpperCase())],
    body,
    theme: 'plain',
    margin: { left: PAD, right: PAD, top: RUN_H + 8, bottom: FOOT_H + 6 },
    styles: { font: FONT, fontSize: 8.5, textColor: rgb(C.body), cellPadding: { top: 2.4, bottom: 2.4, left: 2, right: 2 }, lineColor: rgb(C.ruleLight), lineWidth: { bottom: 0.25 }, valign: 'top', overflow: 'linebreak' },
    headStyles: { fontSize: 7, fontStyle: 'bold', textColor: rgb(C.muted), lineColor: rgb(C.rule), lineWidth: { bottom: 0.3 }, cellPadding: { top: 0, bottom: 2.2, left: 2, right: 2 } },
    columnStyles,
    didParseCell: (data) => {
      const raw = data.cell.raw;
      if (data.section === 'body' && raw && raw.tagColor) data.cell.text = [''];
      if (data.section === 'body' && raw && raw.missing) data.cell.styles.textColor = rgb(C.muted);
    },
    didDrawCell: (data) => {
      const raw = data.cell.raw;
      if (data.section === 'body' && raw && raw.tagColor) tag(doc, raw.content, data.cell.x + 2, data.cell.y + 1.9, raw.tagColor);
    },
  });
  return doc.lastAutoTable.finalY;
};

const activityText = (a) => (!a.note || a.note.toLowerCase().startsWith(a.name.toLowerCase()) ? a.note || a.name : `${a.name} — ${a.note}`);
const T = (content, color) => ({ content, tagColor: color });
const M = (content) => ({ content, missing: true });
const timeCol = { cellWidth: 28, textColor: rgb(C.muted), fontSize: 8 };
const nameCol = (cellWidth) => ({ cellWidth, textColor: rgb(C.ink), fontStyle: 'bold' });

const emptyLine = (doc, y, message) => {
  text(doc, message, PAD, y + 7, { size: 8.5, color: C.muted });
  return y + 10;
};

// Avviso esplicito se il limite di sicurezza dei record è stato raggiunto
const truncationNotice = (model) => (model.truncated.length
  ? `Attenzione: per ${model.truncated.join(', ')} è stato raggiunto il limite di ${model.rowLimit} record per tipo; questo report non li comprende tutti.`
  : '');

const drawNotice = (doc, y, message) => {
  const nl = lines(doc, message, CONTENT_W - 10, 8.5);
  const h = 5 + nl.length * lineH(8.5, 1.4);
  fill(doc, '#FDF7F6');
  doc.roundedRect(PAD, y, CONTENT_W, h, 2, 2, 'F');
  text(doc, nl, PAD + 5, y + 5, { size: 8.5, color: C.sos, bold: true });
  return y + h;
};

// ---------- pagina 1 ----------
const drawMasthead = (doc, period) => {
  fill(doc, C.brand);
  doc.rect(0, 0, PAGE_W, 32, 'F');
  doc.saveGraphicsState();
  doc.rect(0, 0, PAGE_W, 32, null);
  doc.clip();
  doc.discardPath();
  withOpacity(doc, 0.07, () => { fill(doc, C.white); doc.circle(192, 12, 36, 'F'); });
  doc.restoreGraphicsState();
  withOpacity(doc, 0.16, () => { fill(doc, C.white); doc.circle(PAD + 5.5, 16, 5.5, 'F'); });
  text(doc, '♥', PAD + 5.5, 18.2, { size: 13, color: C.white, align: 'center' });
  text(doc, 'CuraCheck', PAD + 15, 15.6, { size: 16, color: C.white, bold: true });
  text(doc, TAGLINE, PAD + 15, 20.4, { size: 7.5, color: '#DCE6DF', charSpace: 0.15 });
  text(doc, 'REPORT SETTIMANALE', PAGE_W - PAD, 14, { size: 7.5, color: '#DCE6DF', align: 'right', charSpace: 0.5 });
  text(doc, period, PAGE_W - PAD, 19.5, { size: 10, color: C.white, bold: true, align: 'right' });
};

const drawRings = (doc, y, s) => {
  const w = (CONTENT_W - 12) / 3;
  const cards = [
    s.dosesExpected
      ? { frac: s.dosesGiven / s.dosesExpected, value: `${Math.round(Math.min(1, s.dosesGiven / s.dosesExpected) * 100)}%`, sub: `${s.dosesGiven} su ${s.dosesExpected}`, color: C.farmaci, track: '#E3EAE5', cap: 'Medicine confermate',
        sm: s.dosesExpected > s.dosesGiven ? `${s.dosesExpected - s.dosesGiven} dosi non confermate` : 'Tutte le dosi confermate' }
      : { frac: 0, value: `${s.dosesGiven}`, sub: 'registrate', color: C.farmaci, track: '#E3EAE5', cap: 'Medicine', sm: 'Piano terapeutico non impostato' },
    { frac: s.mealsExpected ? s.mealsGiven / s.mealsExpected : 0, value: `${s.mealsExpected ? Math.round((s.mealsGiven / s.mealsExpected) * 100) : 0}%`, sub: `${s.mealsGiven} su ${s.mealsExpected}`, color: C.pasti, track: '#F1E8D8', cap: 'Pasti registrati',
      sm: s.mealsMissing.length ? `Non registrati: ${s.mealsMissing.map(([t, n]) => `${n} ${n === 1 ? t.toLowerCase() : s.mealPlural[t]}`).join(', ')}` : 'Tutti i pasti registrati' },
    { frac: s.sereneDays / s.D, value: `${s.sereneDays}/${s.D}`, sub: 'giorni', color: C.umore, track: '#DFEDE6', cap: 'Giornate serene',
      sm: s.moodDays ? [s.neutralDays && `${s.neutralDays} ${s.neutralDays === 1 ? 'giorno neutro' : 'giorni neutri'}`, s.sadDays && `${s.sadDays} ${s.sadDays === 1 ? 'giorno triste' : 'giorni tristi'}`].filter(Boolean).join(' · ') || 'Umore sereno ogni giorno rilevato' : "Nessuna rilevazione dell'umore" },
  ];
  cards.forEach((c, i) => {
    const x = PAD + i * (w + 6);
    fill(doc, C.cream);
    doc.roundedRect(x, y, w, 50, 3, 3, 'F');
    const cx = x + w / 2;
    ring(doc, cx, y + 21, 12, c.frac, c.color, c.track, 3.3);
    text(doc, c.value, cx, y + 21.6, { size: 13, color: C.ink, bold: true, align: 'center' });
    text(doc, c.sub, cx, y + 25.4, { size: 6.5, color: C.muted, align: 'center' });
    text(doc, c.cap, cx, y + 40, { size: 8.5, color: C.ink, bold: true, align: 'center' });
    text(doc, c.sm, cx, y + 44.5, { size: 7.5, color: C.muted, align: 'center', maxWidth: w - 4 });
  });
  return y + 50;
};

const drawStrip = (doc, y, model, s) => {
  const perRow = 7;
  const gap = 2.5;
  const w = (CONTENT_W - gap * (perRow - 1)) / perRow;
  const dotsFor = (k) => [
    ...model.medicines.filter((m) => dayKey(m.time) === k).map(() => C.farmaci),
    ...model.meals.filter((m) => dayKey(m.time) === k).map(() => C.pasti),
    ...model.activities.filter((a) => dayKey(a.time) === k).map(() => C.attivita),
    ...model.walks.filter((w2) => dayKey(w2.start) === k).map(() => C.passo),
    ...model.sos.filter((a) => dayKey(a.time) === k).map(() => C.sos),
  ];
  const MAX = 9;
  for (let r = 0; r < model.dayKeys.length; r += perRow) {
    const keys = model.dayKeys.slice(r, r + perRow);
    const dots = keys.map(dotsFor);
    const rows = Math.max(1, ...dots.map((d) => Math.min(d.length, MAX) + (d.length > MAX ? 1 : 0)));
    const h = 11 + 6.5 + rows * 5 + 6;
    keys.forEach((k, i) => {
      const x = PAD + i * (w + gap);
      const d = keyToDate(k);
      fill(doc, C.white);
      stroke(doc, C.rule, 0.25);
      doc.roundedRect(x, y, w, h, 2.5, 2.5, 'FD');
      fill(doc, C.tint);
      doc.rect(x + 0.15, y + 2.5, w - 0.3, 8.5, 'F');
      doc.roundedRect(x + 0.15, y + 0.15, w - 0.3, 5, 2.4, 2.4, 'F');
      doc.line(x, y + 11, x + w, y + 11);
      text(doc, weekdayShort(d), x + w / 2, y + 4.3, { size: 7, color: C.muted, align: 'center', charSpace: 0.2 });
      text(doc, dayNum(d), x + w / 2, y + 9.2, { size: 11, color: C.ink, bold: true, align: 'center' });
      dots[i].slice(0, MAX).forEach((color, j) => { fill(doc, color); doc.circle(x + w / 2, y + 14.5 + j * 5, 1.7, 'F'); });
      if (dots[i].length > MAX) text(doc, `+${dots[i].length - MAX}`, x + w / 2, y + 15.6 + MAX * 5, { size: 7, color: C.muted, align: 'center' });
      doc.line(x, y + h - 6, x + w, y + h - 6);
      const mood = s.dayMoods[model.dayKeys.indexOf(k)];
      text(doc, mood ? MOOD_NAME[mood] : '—', x + w / 2, y + h - 2, { size: 6.5, color: mood === 'serena' ? C.umore : mood === 'triste' ? C.sos : C.muted, align: 'center' });
    });
    y += h + 3;
  }
  const legend = [['Medicine', C.farmaci], ['Pasti', C.pasti], ['Attività', C.attivita], ['Passeggiata', C.passo], ['Allarme Scudo', C.sos]];
  let x = PAD;
  legend.forEach(([label, color]) => {
    fill(doc, color);
    doc.circle(x + 1.2, y + 2, 1.2, 'F');
    text(doc, label, x + 4, y + 3, { size: 7.5, color: C.muted });
    x += 4 + doc.getTextWidth(label) + 6;
  });
  return y + 6;
};

const drawSosBox = (doc, y, model) => {
  const first = model.sos[0];
  const title = model.sos.length === 1
    ? `Un allarme Scudo, ${weekdayName(first.time).toLowerCase()} ${dayNum(first.time)} alle ${time(first.time)}`
    : `${model.sos.length} allarmi Scudo nel periodo`;
  const body = model.sos.length === 1
    ? 'La famiglia ha ricevuto la notifica al momento dell\'allarme. Il sistema non registra quando l\'evento viene chiuso.'
    : `${model.sos.map((a) => `${weekdayName(a.time).toLowerCase()} ${dayNum(a.time)} alle ${time(a.time)}`).join(', ')}. Il sistema non registra quando gli eventi vengono chiusi.`;
  const bodyLines = lines(doc, body, CONTENT_W - 14, 9);
  const h = 12 + bodyLines.length * lineH(9, 1.5);
  fill(doc, '#FDF7F6');
  stroke(doc, '#EAD2CF', 0.25);
  doc.roundedRect(PAD, y, CONTENT_W, h, 2.5, 2.5, 'FD');
  fill(doc, C.sos);
  doc.rect(PAD, y, 1.25, h, 'F');
  text(doc, title, PAD + 6, y + 6.5, { size: 9.5, color: C.sos, bold: true });
  text(doc, bodyLines, PAD + 6, y + 11.5, { size: 9, color: C.body });
};

const drawPageOne = (doc, model, s, period) => {
  drawMasthead(doc, period);
  let y = 32 + 12;
  const who = [model.patientName, model.familyName && `Famiglia ${model.familyName.replace(/^famiglia\s+/i, '')}`, model.caregiverName && `assistente ${model.caregiverName}`].filter(Boolean).join(' · ');
  text(doc, who.toUpperCase(), PAD, y, { size: 8, color: C.muted, charSpace: 0.4 });
  y += 11;
  if (s.verdict) {
    const start = s.periodWord === 'settimana' ? 'È stata una settimana ' : 'È stato un periodo ';
    text(doc, start, PAD, y, { size: 27, color: C.ink, bold: true });
    const w1 = doc.getTextWidth(start);
    text(doc, s.verdict, PAD + w1, y, { size: 27, color: C.brand, bold: true });
    text(doc, '.', PAD + w1 + doc.getTextWidth(s.verdict), y, { size: 27, color: C.ink, bold: true });
  } else {
    text(doc, 'Nessuna attività registrata.', PAD, y, { size: 24, color: C.ink, bold: true });
  }
  const sub = s.facts.length ? s.facts.join(' ') : "Non ci sono conferme dell'assistente in questo periodo.";
  const subLines = lines(doc, sub, 140, 10.5);
  text(doc, subLines, PAD, y + 9, { size: 10.5, color: C.body });
  y += 9 + subLines.length * lineH(10.5, 1.55) + 6;
  if (truncationNotice(model)) y = drawNotice(doc, y - 2, truncationNotice(model)) + 4;
  y = drawRings(doc, y, s) + 13;
  sectionHead(doc, y, "La settimana a colpo d'occhio", `Ogni pallino è una conferma di ${model.caregiverName || "dell'assistente"}`.replace('di dell', 'dell'));
  y = drawStrip(doc, y + 7, model, s) + 6;
  if (model.sos.length) drawSosBox(doc, y, model);
};

// ---------- diario ----------
const eventHeight = (doc, e) => {
  const t = lines(doc, e.title, CONTENT_W - 26, 9.5).length * lineH(9.5);
  const sm = e.small ? lines(doc, e.small, CONTENT_W - 26, 8.5).length * lineH(8.5) + 0.8 : 0;
  return Math.max(8, t + sm + 3.6);
};

const drawEvent = (doc, y, e, first, last) => {
  const color = e.color || KIND[e.kind].color;
  const h = eventHeight(doc, e);
  text(doc, time(e.time), PAD, y + 3.4, { size: 8, color: C.muted });
  const rx = PAD + 16 + 3;
  stroke(doc, C.rule, 0.28);
  doc.line(rx, first ? y + 2.4 : y, rx, last ? y + 2.4 : y + h);
  fill(doc, C.white);
  doc.circle(rx, y + 3.4, 2.4, 'F');
  fill(doc, color);
  doc.circle(rx, y + 3.4, 1.8, 'F');
  const tl = lines(doc, e.title, CONTENT_W - 26, 9.5);
  text(doc, tl, PAD + 24, y + 3.6, { size: 9.5, color: C.ink });
  if (e.small) text(doc, lines(doc, e.small, CONTENT_W - 26, 8.5), PAD + 24, y + 3.6 + tl.length * lineH(9.5) + 0.8, { size: 8.5, color: C.muted });
  return y + h;
};

// Disegna (o misura, con dry) il diario giorno per giorno; restituisce le pagine usate
const layoutDiary = (doc, model, s, events, cg, dry) => {
  const pages = [];
  let page = null;
  let y = 0;
  const newPage = () => {
    if (!dry) doc.addPage();
    page = { days: [] };
    pages.push(page);
    y = RUN_H + 8 + 6;
    if (!dry) sectionHead(doc, y, 'Il diario della settimana', '');
    page.headY = y;
    y += 4;
  };
  newPage();
  model.dayKeys.forEach((k) => {
    const dayEvents = events.filter((e) => dayKey(e.time) === k);
    const d = keyToDate(k);
    const span = dayEvents.length ? `${cg} · conferme dalle ${time(dayEvents[0].time)} alle ${time(dayEvents[dayEvents.length - 1].time)}` : 'Nessuna conferma registrata';
    const head = (cont) => {
      if (y + 10 + dayEvents.slice(0, 2).reduce((h, e) => h + eventHeight(doc, e), 0) > BOTTOM) newPage();
      y += 7;
      if (!dry) {
        const label = `${dayLong(d)}${cont ? ' (segue)' : ''}`;
        text(doc, label, PAD, y, { size: 11.5, color: C.ink, bold: true });
        const w = doc.getTextWidth(label);
        text(doc, span, PAD + w + 3, y, { size: 8, color: C.muted });
        const lx = PAD + w + 3 + doc.getTextWidth(span) + 2;
        stroke(doc, C.rule, 0.25);
        if (lx < PAGE_W - PAD) doc.line(lx, y - 1, PAGE_W - PAD, y - 1);
      }
      if (!page.days.includes(d)) page.days.push(d);
      y += 3.5;
    };
    head(false);
    dayEvents.forEach((e, i) => {
      const h = eventHeight(doc, e);
      if (y + h > BOTTOM) { newPage(); head(true); }
      if (dry) y += h;
      else y = drawEvent(doc, y, e, i === 0, i === dayEvents.length - 1);
    });
  });
  const watch = s.watch.length ? s.watch.join(' ') : 'Nessun punto di attenzione emerso dai dati di questo periodo.';
  const wl = lines(doc, watch, CONTENT_W - 14, 9);
  const boxH = 13 + wl.length * lineH(9, 1.5);
  if (y + 8 + boxH > BOTTOM) newPage();
  if (!dry) {
    const by = Math.max(y + 8, BOTTOM - boxH);
    fill(doc, C.tint);
    doc.roundedRect(PAD, by, CONTENT_W, boxH, 3, 3, 'F');
    text(doc, 'Cosa guardare la prossima settimana', PAD + 7, by + 7.5, { size: 10, color: C.ink, bold: true });
    text(doc, wl, PAD + 7, by + 12.8, { size: 9, color: C.body });
  }
  return pages;
};

const labelDiaryRanges = (doc, pages, firstPage) => {
  pages.forEach((p, i) => {
    if (!p.days.length) return;
    const a = weekdayName(p.days[0]);
    const b = weekdayName(p.days[p.days.length - 1]);
    doc.setPage(firstPage + i);
    text(doc, a === b ? a : `${a} – ${b}`, PAGE_W - PAD, p.headY, { size: 8, color: C.muted, align: 'right' });
  });
};

// ---------- passeggiate ----------
const drawWalks = async (doc, model, s) => {
  doc.addPage();
  let y = sectionHead(doc, RUN_H + 14, 'Le passeggiate', s.walkSummary) + 2;
  if (!model.walks.length) return emptyLine(doc, y, 'Nessuna passeggiata registrata in questo periodo.');
  const mapped = model.walks.filter((w) => w.pins.length).slice(0, MAPPED_WALKS);
  const images = await Promise.all(mapped.map(fetchMapImage));
  mapped.forEach((w, i) => {
    y += 6;
    stroke(doc, C.rule, 0.25);
    fill(doc, C.cream);
    doc.roundedRect(PAD, y, CONTENT_W, 12 + 54 + 6, 3, 3, 'FD');
    text(doc, dayLong(w.start), PAD + 5, y + 7.5, { size: 10, color: C.ink, bold: true });
    const dw = doc.getTextWidth(dayLong(w.start));
    text(doc, `${time(w.start)}${w.end ? ` – ${time(w.end)}` : ''}`, PAD + 5 + dw + 5, y + 7.5, { size: 8.5, color: C.muted });
    if (w.minutes != null) text(doc, minutesLabel(w.minutes), PAGE_W - PAD - 5, y + 7.5, { size: 9, color: C.brand, bold: true, align: 'right' });
    doc.line(PAD, y + 12, PAGE_W - PAD, y + 12);
    if (images[i]) {
      doc.addImage(images[i], 'PNG', PAD + 0.2, y + 12.1, CONTENT_W - 0.4, 53.7);
    } else {
      fill(doc, '#EDEAE3');
      doc.rect(PAD + 0.2, y + 12.1, CONTENT_W - 0.4, 53.7, 'F');
      text(doc, 'Mappa non disponibile', PAD + CONTENT_W / 2, y + 40, { size: 8.5, color: C.muted, align: 'center' });
    }
    const hasEnd = w.pins.some((p) => p.label === 'Rientro');
    text(doc, `P = partenza${hasEnd ? ' · R = rientro' : ''} · zona dei punti registrati, nessun percorso tracciato`, PAD + 5, y + 70, { size: 7, color: C.muted });
    y += 72;
  });
  const rest = model.walks.filter((w) => !mapped.includes(w));
  if (!rest.length) return y;
  return table(doc, y + 5, ['Data', 'Orario', 'Durata', 'Zona'], rest.map((w) => [
    dayLong(w.start), `${time(w.start)}${w.end ? ` – ${time(w.end)}` : ''}`,
    w.minutes != null ? minutesLabel(w.minutes) : M('Non registrata'), w.place || M('Luogo non disponibile'),
  ]), { 0: nameCol(46), 1: { cellWidth: 36, textColor: rgb(C.muted), fontSize: 8 }, 2: { cellWidth: 28 } });
};

// ---------- pagine di dettaglio ----------
const nextSection = (doc, y, needed = 40) => {
  if (y + needed > BOTTOM) { doc.addPage(); return RUN_H + 14; }
  return y + 12;
};

const drawMedicineMeals = (doc, model, s) => {
  doc.addPage();
  let y = sectionHead(doc, RUN_H + 14, 'Dettaglio medicine', s.dosesExpected ? `${s.dosesGiven} somministrazioni su ${s.dosesExpected}` : `${s.dosesGiven} somministrazioni`);
  y = model.medicines.length
    ? table(doc, y + 5, ['Data e ora', 'Farmaco', 'Dosaggio', 'Note'], model.medicines.map((m) => [shortStamp(m.time), m.name, m.dose || M('Non indicato'), m.note]), { 0: timeCol, 1: nameCol(46), 2: { cellWidth: 24 } })
    : emptyLine(doc, y, 'Nessuna somministrazione registrata in questo periodo.');
  y = nextSection(doc, y);
  y = sectionHead(doc, y, 'Dettaglio pasti', `${s.mealsGiven} pasti su ${s.mealsExpected}`);
  return model.meals.length
    ? table(doc, y + 5, ['Data e ora', 'Pasto', 'Descrizione'], model.meals.map((m) => [shortStamp(m.time), T(m.type, C.pasti), m.description || M('Non descritto')]), { 0: timeCol, 1: { cellWidth: 26 } })
    : emptyLine(doc, y, 'Nessun pasto registrato in questo periodo.');
};

const drawMoodTiles = (doc, y, model) => {
  const umore = model.moods.filter((m) => m.level);
  const w = (CONTENT_W - 10) / 3;
  ['serena', 'neutra', 'triste'].forEach((level, i) => {
    const n = umore.filter((m) => m.level === level).length;
    const x = PAD + i * (w + 5);
    stroke(doc, C.rule, 0.25);
    fill(doc, C.white);
    doc.roundedRect(x, y, w, 21, 2.5, 2.5, 'FD');
    fill(doc, MOOD_COLOR[level]);
    doc.rect(x + 1, y, w - 2, 0.9, 'F');
    text(doc, String(n), x + 5, y + 10, { size: 19, color: C.ink, bold: true });
    text(doc, MOOD_NAME[level], x + 5, y + 14.5, { size: 8.5, color: MOOD_COLOR[level], bold: true });
    fill(doc, '#EDF1EE');
    doc.roundedRect(x + 5, y + 16.5, w - 10, 2.2, 1.1, 1.1, 'F');
    fill(doc, MOOD_COLOR[level]);
    const pct = umore.length ? n / umore.length : 0;
    doc.roundedRect(x + 5, y + 16.5, Math.max(2.2, (w - 10) * pct), 2.2, 1.1, 1.1, 'F');
  });
  return y + 21;
};

const drawActivitiesPage = (doc, model, cg) => {
  doc.addPage();
  let y = sectionHead(doc, RUN_H + 14, 'Attività svolte', `${model.activities.length} ${model.activities.length === 1 ? 'attività' : 'attività'}`);
  y = model.activities.length
    ? table(doc, y + 5, ['Data e ora', 'Attività', 'Durata'], model.activities.map((a) => [shortStamp(a.time), activityText(a), M('Non registrata')]), { 0: timeCol, 1: { textColor: rgb(C.ink), fontStyle: 'bold' }, 2: { cellWidth: 28 } })
    : emptyLine(doc, y, 'Nessuna attività registrata in questo periodo.');
  y = nextSection(doc, y, 60);
  const umore = model.moods.filter((m) => m.level);
  y = sectionHead(doc, y, "Monitoraggio dell'umore", `${umore.length} ${umore.length === 1 ? 'rilevazione' : 'rilevazioni'}`);
  y = drawMoodTiles(doc, y + 5, model);
  if (model.moods.length) {
    y = table(doc, y + 4, ['Data e ora', 'Umore', `Note di ${cg}`], model.moods.map((m) => [
      shortStamp(m.time), T(m.level ? MOOD_NAME[m.level] : m.label, m.level ? MOOD_COLOR[m.level] : C.nota),
      m.level ? [moodExtra(m), m.note].filter(Boolean).join(' — ') : [`${NOT_MOOD[m.categoria] || 'Altro stato'}, non conteggiato nell'umore`, m.note].filter(Boolean).join(' — '),
    ]), { 0: timeCol, 1: { cellWidth: 30 } });
    const excluded = model.moods.filter((m) => !m.level).length;
    if (excluded) {
      const note = excluded === 1
        ? `La riga segnata «non conteggiato nell'umore» è uno stato fisico o di appetito: compare qui ma non rientra nelle ${umore.length} rilevazioni dell'umore.`
        : `Le ${excluded} righe segnate «non conteggiato nell'umore» sono stati fisici o di appetito: compaiono qui ma non rientrano nelle ${umore.length} rilevazioni dell'umore.`;
      text(doc, note, PAD, y + 4.5, { size: 7.5, color: C.muted, maxWidth: CONTENT_W });
      y += 6;
    }
  }
  y = nextSection(doc, y);
  y = sectionHead(doc, y, 'Visite mediche', `${model.visits.length} ${model.visits.length === 1 ? 'visita' : 'visite'}`);
  y = model.visits.length
    ? table(doc, y + 5, ['Data', 'Medico', 'Specialità', 'Esito'], model.visits.map((v) => [dayMonthYear(v.time), v.doctor, v.specialty || M('Non indicata'), v.outcome || M('Esito non registrato')]), { 0: { cellWidth: 34, textColor: rgb(C.muted), fontSize: 8 }, 1: nameCol(40), 2: { cellWidth: 36 } })
    : emptyLine(doc, y, 'Nessuna visita in calendario per questo periodo.');
  y = nextSection(doc, y);
  y = sectionHead(doc, y, 'Emergenze e allarmi Scudo', model.sos.length ? `${model.sos.length} ${model.sos.length === 1 ? 'allarme' : 'allarmi'}` : 'Nessun allarme');
  if (model.sos.length) {
    table(doc, y + 5, ['Data e ora', 'Tipo', 'Descrizione', 'Stato'], model.sos.map((a) => [shortStamp(a.time), T(a.kind, C.sos), a.description, M('Chiusura non registrata')]), { 0: timeCol, 1: { cellWidth: 26 }, 3: { cellWidth: 32 } });
  } else {
    emptyLine(doc, y, 'Nessun allarme Scudo in questo periodo.');
  }
};

const drawRegister = (doc, model, events, cg) => {
  doc.addPage();
  const y = sectionHead(doc, RUN_H + 14, 'Registro completo', `Tutte le notifiche inviate da ${cg}`.replace("da l'assistente", "dall'assistente"));
  if (!events.length) return emptyLine(doc, y, 'Nessuna notifica in questo periodo.');
  const notice = truncationNotice(model);
  return table(doc, notice ? drawNotice(doc, y + 4, notice) + 3 : y + 5, ['Data e ora', 'Tipo', 'Dettaglio'], events.map((e) => [
    shortStamp(e.time), T(KIND[e.kind].label, e.color || KIND[e.kind].color), e.small ? `${e.title} — ${e.small}` : e.title,
  ]), { 0: timeCol, 1: { cellWidth: 30 } });
};

// ---------- testate e piè di pagina ----------
const drawChrome = (doc, model, period) => {
  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i += 1) {
    doc.setPage(i);
    if (i > 1) {
      text(doc, 'CuraCheck', PAD, 9.4, { size: 9.5, color: C.brand, bold: true });
      text(doc, `${model.patientName} · ${period}`, PAGE_W - PAD, 9.4, { size: 8, color: C.muted, align: 'right' });
      stroke(doc, C.rule, 0.25);
      doc.line(0, RUN_H, PAGE_W, RUN_H);
    }
    stroke(doc, C.rule, 0.25);
    doc.line(0, PAGE_H - FOOT_H, PAGE_W, PAGE_H - FOOT_H);
    text(doc, `CuraCheck · ${TAGLINE}`, PAD, PAGE_H - 5.6, { size: 7.5, color: C.muted });
    text(doc, `Pagina ${i} di ${total}`, PAGE_W - PAD, PAGE_H - 5.6, { size: 7.5, color: C.muted, align: 'right' });
  }
};

export const buildReportDoc = async (model) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  await loadFonts(doc);
  const s = summarize(model);
  const cg = model.caregiverName || "l'assistente";
  const period = periodLabel(model.dayKeys[0], model.dayKeys[model.dayKeys.length - 1]);
  drawPageOne(doc, model, s, period);
  const diaryPages = layoutDiary(doc, model, s, buildEvents(model, cg, null), cg, true).length;
  const events = buildEvents(model, cg, 2 + diaryPages);
  const pages = layoutDiary(doc, model, s, events, cg, false);
  labelDiaryRanges(doc, pages, 2);
  await drawWalks(doc, model, s);
  drawMedicineMeals(doc, model, s);
  drawActivitiesPage(doc, model, cg);
  drawRegister(doc, model, events, cg);
  drawChrome(doc, model, period);
  return doc;
};

/**
 * Genera e scarica il report PDF dal modello di fetchReportModel()
 */
export const generatePDFReport = async (model) => {
  try {
    const doc = await buildReportDoc(model);
    const dateStr = new Date().toISOString().split('T')[0];
    const safeName = model.patientName.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^A-Za-z0-9]+/g, '_');
    const filename = `CuraCheck_Report_${safeName}_${dateStr}.pdf`;
    doc.save(filename);
    return filename;
  } catch (error) {
    console.error('PDF Generation Error:', error);
    throw new Error('Errore nella generazione del PDF. Riprova più tardi.');
  }
};

/**
 * Calculate comprehensive statistics
 */
export const calculateStats = (activities = [], options = {}) => {
  const stats = {
    medicines: 0,
    meals: 0,
    wellness: 0,
    notes: 0,
    sos: 0,
    visits: 0,
    moodStats: { felice: 0, neutro: 0, triste: 0 },
    total: activities.length,
  };

  activities.forEach(activity => {
    const type = (activity.action_type || activity.tipo || '').toLowerCase();
    
    switch (type) {
      case 'medicine':
      case 'farmaco':
        stats.medicines++;
        break;
      case 'meal':
      case 'pasto':
        stats.meals++;
        break;
      case 'wellness':
      case 'attivita':
        stats.wellness++;
        break;
      case 'note':
      case 'nota':
        stats.notes++;
        break;
      case 'sos':
      case 'emergency':
        stats.sos++;
        break;
      case 'visita':
      case 'visit':
        stats.visits++;
        break;
      case 'mood':
      case 'umore':
        const mood = (activity.mood || activity.umore || '').toLowerCase();
        if (mood === 'happy' || mood === 'felice') stats.moodStats.felice++;
        else if (mood === 'neutral' || mood === 'neutro') stats.moodStats.neutro++;
        else if (mood === 'sad' || mood === 'triste') stats.moodStats.triste++;
        break;
      default:
        break;
    }
  });

  return stats;
};

export default generatePDFReport;
